<template>
  <div class="price-check-log-page">
    <div class="result-header">
      <div>
        <h3>Search Log Details</h3>
        <div class="header-meta">
          <span class="meta-chip"><small>Booking Id</small><strong>{{ bookingId }}</strong></span>
          <span v-if="ratingDisplay !== '-'" class="meta-chip"><small>Rating</small><strong>{{ ratingDisplay }}</strong></span>
          <span v-if="roomCountDisplay !== '-'" class="meta-chip"><small>Rooms</small><strong>{{ roomCountDisplay }}</strong></span>
        </div>
      </div>
      <b-button class="back-button" variant="outline-primary" @click="goBack">Back</b-button>
    </div>

    <section class="booking-details-section">
      <div class="section-title">
        <h4>
          <button
            v-if="hasBookingDetailsData"
            class="section-expand-button"
            type="button"
            @click="toggleBookingDetails"
          >
            {{ isBookingDetailsExpanded ? '-' : '+' }}
          </button>
          Booking Details
        </h4>
      </div>
      <div v-if="roomTypeFields.length" class="room-grid">
        <div
          v-for="field in roomTypeFields"
          :key="field.label"
          :class="['room-field', { 'cancellation-field': field.type === 'cancellation' }]"
        >
          <span v-if="field.type !== 'stay-dates'">{{ field.label }}</span>
          <div v-if="field.type === 'stay-dates'" class="stay-date-details">
            <div v-for="item in field.value" :key="item.label" class="stay-date-detail">
              <small>{{ item.label }}</small>
              <strong>{{ item.value }}</strong>
            </div>
          </div>
          <div v-if="field.type === 'cancellation'" class="cancellation-details">
            <div v-for="item in field.value" :key="item.label" class="cancellation-detail">
              <small>{{ item.label }}</small>
              <strong>{{ item.value }}</strong>
            </div>
          </div>
          <strong v-if="field.type !== 'stay-dates' && field.type !== 'cancellation'">{{ field.value }}</strong>
        </div>
      </div>
      <div v-if="isBookingDetailsExpanded" class="room-json-panel">
        <pre>{{ formattedBookingDetails }}</pre>
      </div>
      <p v-if="!roomTypeFields.length" class="empty-text">
        {{ isBookingDetailsLoading ? 'Loading booking details...' : 'No booking details available.' }}
      </p>
    </section>

    <section v-if="isLoading" class="log-loader-section">
      <div class="loader-ring"></div>
      <h5>Loading search log</h5>
      <p>Please wait while the listing and response data are fetched.</p>
    </section>

    <template v-else>
    <section class="listing-section">
      <div class="section-title">
        <h4>Listing</h4>
        <span>{{ listingRows.length }} result{{ listingRows.length === 1 ? '' : 's' }}</span>
      </div>

      <div v-if="listingRows.length" class="listing-table-wrap">
        <table class="listing-table">
          <colgroup>
            <col class="hotel-col" />
            <col class="room-type-col" />
            <col class="room-category-col" />
            <col class="refund-col" />
            <col class="meal-col" />
            <col class="supplier-col" />
            <col class="price-col" />
            <col class="profit-col" />
            <col class="deadline-col" />
          </colgroup>
          <thead>
            <tr>
              <th>HotelName</th>
              <th>RoomType</th>
              <th>RoomCategory</th>
              <th>IsRefundable</th>
              <th>MealBasis</th>
              <th>Supplier</th>
              <th>Price</th>
              <th>Profit</th>
              <th>Cancellation Deadline</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, index) in listingRows">
              <tr
                :key="`listing-row-${index}`"
                :class="['listing-row', { 'status-less-row': row.isLessStatus }]"
              >
                <td>
                  <div class="hotel-cell">
                    <button class="expand-icon" type="button" @click.stop="toggleListingData(index)">
                      {{ expandedRow === index ? '-' : '+' }}
                    </button>
                    <strong>{{ row.hotelName }}</strong>
                  </div>
                </td>
                <td>{{ row.roomType }}</td>
                <td>{{ row.roomCategory }}</td>
                <td>
                  <span :class="['refund-pill', row.refundClass]">{{ row.isrefundable }}</span>
                </td>
                <td>{{ row.mealbasis }}</td>
                <td>
                  <div
                    :class="['supplier-policy-wrapper', { 'has-policy': row.hasCancellationPolicy }]"
                    tabindex="0"
                  >
                    <span class="supplier-pill">{{ row.supplier }}</span>
                    <span
                      v-if="row.hasCancellationPolicy"
                      :id="`supplier-policy-trigger-${index}`"
                      class="supplier-policy-trigger"
                    >
                      i
                    </span>
                    <b-popover
                      v-if="row.hasCancellationPolicy"
                      :target="`supplier-policy-trigger-${index}`"
                      triggers="hover focus"
                      placement="bottom"
                      container="body"
                      custom-class="supplier-policy-popover"
                    >
                      <template #title>
                        <div class="supplier-policy-title">
                          <span>Cancellation Details</span>
                          <span>⌄</span>
                        </div>
                      </template>
                      <div
                        v-for="policyItem in row.cancellationPolicy"
                        :key="policyItem.label"
                        class="supplier-policy-row"
                      >
                        <span>{{ policyItem.label }}:</span>
                        <strong>{{ policyItem.value }}</strong>
                      </div>
                    </b-popover>
                  </div>
                </td>
                <td><span class="price-badge">{{ row.price }}</span></td>
                <td><span class="profit-badge">{{ row.profit }}</span></td>
                <!-- {{ row }} -->
                <td class="deadline-cell">{{ formatDateTimelisting(row.cancellationStartDate) }}</td>
              </tr>
              <tr v-if="expandedRow === index" :key="`listing-json-${index}`">
                <td colspan="9" class="expanded-json-cell">
                  <pre>{{ row.expandedJson }}</pre>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <p v-else class="empty-text">No listing details available.</p>
    </section>

    <section class="data-section">
      <div class="section-title data-title">
        <h4>Data</h4>
        <div class="data-search-tools">
          <input
            v-model.trim="dataSearch"
            class="data-search"
            type="text"
            placeholder="Search response"
          />
          <span v-if="dataSearch" class="data-match-count">{{ activeDataMatchLabel }}</span>
          <button
            class="data-search-nav"
            type="button"
            :disabled="!dataMatchCount"
            title="Previous match"
            @click="goToDataMatch(-1)"
          >
            ↑
          </button>
          <button
            class="data-search-nav"
            type="button"
            :disabled="!dataMatchCount"
            title="Next match"
            @click="goToDataMatch(1)"
          >
            ↓
          </button>
        </div>
      </div>
      <div ref="dataJsonPanel" class="json-panel">
        <pre v-html="highlightedData"></pre>
      </div>
    </section>
    </template>
  </div>
</template>

<script>
import axios from '@axios'
import { BButton, BPopover } from 'bootstrap-vue'

export default {
  components: {
    BButton,
    BPopover,
  },
  data() {
    return {
      bookingId: this.$route.params.bookingId || '',
      requestId: this.$route.query.request_id || this.$route.params.request_id || '',
      logData: {},
      bookingDetails: {},
      isLoading: false,
      isBookingDetailsLoading: false,
      isBookingDetailsExpanded: false,
      expandedRow: null,
      dataSearch: '',
      activeDataMatchIndex: 0,
    }
  },
  computed: {
    hasBookingDetailsData() {
      return Object.keys(this.bookingDetails || {}).length > 0
    },
    ratingDisplay() {
      return this.getFirstFieldValue(
        [
          this.bookingDetails.room_type || {},
          this.bookingDetails.booking || {},
        ],
        ['ratings', 'rating', 'hotel_rating', 'HotelRating'],
      )
    },
    roomCountDisplay() {
      return this.getFirstFieldValue(
        [
          this.bookingDetails.room_type || {},
          this.bookingDetails.booking || {},
        ],
        ['number_of_rooms', 'numberOfRooms', 'NumberOfRooms'],
      )
    },
    roomTypeFields() {
      const roomType = this.bookingDetails.room_type || {}
      const booking = this.bookingDetails.booking || {}
      const displayFields = [
        { label: 'Hotel Id', paths: ['hotel_id'] },
        { label: 'Hotel Name', paths: ['hotel_name', 'hotelName', 'HotelName', 'hotel.name', 'hotel.hotel_name', 'HotelDetails.HotelName'] },
        { label: 'Location', paths: ['city_name', 'CityName', 'cityName'], formatter: () => this.formatLocation(roomType, booking) },
        { label: 'Address', paths: ['HotelAddress'] },
        { label: 'Nationality', paths: ['GuestNationality'] },
        { label: 'Residence', paths: ['CountryOfResidence'] },
        { label: 'Supplier', paths: ['OriginalSupplier'] },
        { label: 'Check-in / Check-out', type: 'stay-dates', paths: ['checkin'], formatter: () => this.formatStayDates(roomType, booking) },
        { label: 'Room Category', paths: ['room_category'] },
        { label: 'Meal Basis', paths: ['room_basis'] },
        { label: 'Room Description', paths: ['room_description'] },
        { label: 'Price', paths: ['price'], formatter: value => this.formatPrice(value, roomType.currency) },
        {
          label: 'Cancellation Details',
          type: 'cancellation',
          paths: ['SupplierCancellationDetails', 'supplier_from_date', 'cancellation_details', 'cancellation_policy', 'CancellationPolicy', 'CancellationPolicies', 'Policy.CancellationPolicy'],
          formatter: () => this.formatSupplierCancellationDetails(roomType, booking),
        },
      ]
      return displayFields
        .map(field => {
          const value = this.getFirstFieldValue([roomType, booking], field.paths, field.formatter)
          return {
            label: field.label,
            type: field.type || '',
            value,
          }
        })
        .filter(field => field.value !== '-' && (!Array.isArray(field.value) || field.value.length))
    },
    formattedBookingDetails() {
      return JSON.stringify(this.bookingDetails.booking || this.bookingDetails || {}, null, 2)
    },
    listingRows() {
      const listing = Array.isArray(this.logData.filtered_response) ? this.logData.filtered_response : []
      return [...listing].sort(this.sortByConvertedPrice).map(item => {
        const roomRate = Array.isArray(item.RoomRates) && item.RoomRates.length ? item.RoomRates[0] : {}
        const cancellationPolicy = this.formatListingCancellationPolicy(item)
        return {
          hotelName: this.formatValue(item.HotelName),
          roomType: this.formatValue(roomRate.RoomType),
          roomCategory: this.formatValue(roomRate.RoomCategory),
          price: this.formatPrice(item.SupplierRoomRateConverted, item.SupplierCurrencyCodeConverted),
          profit: this.formatProfit(item),
          isrefundable: this.getFirstFieldValue([item, roomRate], ['IsRefundable', 'isrefundable', 'Refundable']),
          refundClass: this.getRefundClass(this.getFirstFieldValue([item, roomRate], ['IsRefundable', 'isrefundable', 'Refundable'])),
          mealbasis: this.getFirstFieldValue([item, roomRate], ['MealBasis', 'MealPlan', 'MealPlanCode', 'BoardBasis']),
          supplier: this.formatValue(item.SupplierName),
          cancellationPolicy,
          hasCancellationPolicy: cancellationPolicy.length > 0,
          isLessStatus: String(item.Status || '').toLowerCase() === 'lower',
          expandedJson: JSON.stringify(item, null, 2),
          cancellationStartDate: this.getFirstFieldValue([item, roomRate], ['CancellationPolicyListStart']),
        }
      })
    },
    responseData() {
      const response = this.logData.response || {}
      if (response && typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'data')) {
        return response.data
      }
      return response
    },
    formattedData() {
      return JSON.stringify(this.responseData || {}, null, 2)
    },
    dataMatchCount() {
      if (!this.dataSearch) {
        return 0
      }
      const matches = this.formattedData.match(new RegExp(this.escapeRegExp(this.dataSearch), 'gi'))
      return matches ? matches.length : 0
    },
    activeDataMatchLabel() {
      if (!this.dataMatchCount) {
        return '0/0'
      }
      return `${this.activeDataMatchIndex + 1}/${this.dataMatchCount}`
    },
    highlightedData() {
      const escapedData = this.escapeHtml(this.formattedData)
      const escapedQuery = this.escapeRegExp(this.dataSearch)
      if (!escapedQuery) {
        return escapedData
      }

      let index = -1
      return escapedData.replace(
        new RegExp(escapedQuery, 'gi'),
        match => {
          index += 1
          const activeClass = index === this.activeDataMatchIndex ? ' is-active' : ''
          return `<mark class="json-highlight${activeClass}" data-match-index="${index}">${match}</mark>`
        },
      )
    },
  },
  watch: {
    dataSearch() {
      this.activeDataMatchIndex = 0
      this.$nextTick(() => this.scrollToActiveDataMatch())
    },
    highlightedData() {
      this.$nextTick(() => this.scrollToActiveDataMatch())
    },
  },
  mounted() {
    this.restoreLogRouteContext()
    this.fetchBookingDetails()
    this.fetchLogDetails()
  },
  methods: {
    logRouteContextKey() {
      return `price-check-log-context-${this.bookingId}`
    },
    restoreLogRouteContext() {
      if (this.requestId) {
        sessionStorage.setItem(this.logRouteContextKey(), JSON.stringify({
          requestId: this.requestId,
        }))
        return
      }

      const cachedContext = sessionStorage.getItem(this.logRouteContextKey())
      if (!cachedContext) {
        return
      }
      try {
        const context = JSON.parse(cachedContext)
        this.requestId = context.requestId || ''
      } catch (error) {
        sessionStorage.removeItem(this.logRouteContextKey())
      }
    },
    fetchBookingDetails() {
      if (!this.bookingId) {
        return
      }

      this.isBookingDetailsLoading = true
      axios.post('price-check/bookings/view', {
        booking_id: this.bookingId,
      }).then(response => {
        this.bookingDetails = response.data || {}
      }).catch(error => {
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Failed to fetch booking details.'
        this.$bvToast.toast(message, {
          title: 'Price Check',
          variant: 'danger',
          solid: true,
        })
      }).finally(() => {
        this.isBookingDetailsLoading = false
      })
    },
    fetchLogDetails() {
      const detailRequest = {
        request_id: this.requestId,
      }
      if (!detailRequest.request_id) {
        this.$bvToast.toast('Request id is missing.', {
          title: 'Price Check Logs',
          variant: 'danger',
          solid: true,
        })
        return
      }

      this.isLoading = true
      axios.post('price-check/bookings/log-detail', detailRequest).then(response => {
        this.logData = response.data || {}
      }).catch(error => {
        const message = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : 'Failed to fetch booking log details.'
        this.$bvToast.toast(message, {
          title: 'Price Check Logs',
          variant: 'danger',
          solid: true,
        })
      }).finally(() => {
        this.isLoading = false
      })
    },
    toggleListingData(index) {
      this.expandedRow = this.expandedRow === index ? null : index
    },
    toggleBookingDetails() {
      this.isBookingDetailsExpanded = !this.isBookingDetailsExpanded
    },
    goToDataMatch(direction) {
      if (!this.dataMatchCount) {
        return
      }
      const nextIndex = this.activeDataMatchIndex + direction
      this.activeDataMatchIndex = (nextIndex + this.dataMatchCount) % this.dataMatchCount
      this.$nextTick(() => this.scrollToActiveDataMatch())
    },
    scrollToActiveDataMatch() {
      if (!this.dataMatchCount || !this.$refs.dataJsonPanel) {
        return
      }
      const match = this.$refs.dataJsonPanel.querySelector(`mark[data-match-index="${this.activeDataMatchIndex}"]`)
      if (match) {
        match.scrollIntoView({ block: 'center', inline: 'nearest' })
      }
    },
    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    escapeRegExp(value) {
      return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    formatLabel(key) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, letter => letter.toUpperCase())
    },
    formatValue(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      return value
    },
    formatDisplayDate(value) {
      const formattedValue = this.formatValue(value)
      if (formattedValue === '-') {
        return '-'
      }

      const valueText = String(formattedValue).trim()
      const isoMatch = valueText.match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`
      }

      const slashMatch = valueText.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
      if (slashMatch) {
        const day = slashMatch[1].padStart(2, '0')
        const month = slashMatch[2].padStart(2, '0')
        return `${slashMatch[3]}-${month}-${day}`
      }

      return formattedValue
    },
    formatComplexValue(value) {
      if (value === null || value === undefined || value === '') {
        return '-'
      }
      if (Array.isArray(value)) {
        return value.map(item => this.formatComplexValue(item)).filter(item => item !== '-').join('; ') || '-'
      }
      if (typeof value === 'object') {
        return Object.entries(value)
          .map(([key, item]) => `${this.formatLabel(key)}: ${this.formatComplexValue(item)}`)
          .filter(item => !item.endsWith(': -'))
          .join('; ') || '-'
      }
      return value
    },
    getFirstFieldValue(sources, paths, formatter) {
      for (const source of sources) {
        for (const path of paths) {
          const value = this.getPathValue(source, path)
          const formattedValue = formatter ? formatter(value) : this.formatValue(value)
          if (formattedValue !== '-') {
            return formattedValue
          }
        }
      }
      return '-'
    },
    getPathValue(source, path) {
      if (!source || typeof source !== 'object') {
        return undefined
      }
      return path.split('.').reduce((current, key) => {
        if (current && typeof current === 'object' && Object.prototype.hasOwnProperty.call(current, key)) {
          return current[key]
        }
        return undefined
      }, source)
    },
    formatPrice(rate, currency) {
      const formattedRate = this.formatValue(rate)
      const formattedCurrency = this.formatValue(currency)
      if (formattedRate === '-' && formattedCurrency === '-') {
        return '-'
      }
      if (formattedCurrency === '-') {
        return formattedRate
      }
      if (formattedRate === '-') {
        return formattedCurrency
      }
      return `${formattedRate} ${formattedCurrency}`
    },
    formatLocation(roomType, booking) {
      const city = this.getFirstFieldValue([roomType, booking], ['city_name', 'CityName', 'cityName'])
      const country = this.getFirstFieldValue([roomType, booking], ['country_name', 'CountryName', 'countryName'])
      if (city === '-' && country === '-') {
        return '-'
      }
      if (city === '-') {
        return country
      }
      if (country === '-') {
        return city
      }
      return `${city} (${country})`
    },
    formatStayDates(roomType, booking) {
      const checkin = this.getFirstFieldValue([roomType, booking], ['checkin', 'checkin_date', 'CheckIn'])
      const checkout = this.getFirstFieldValue([roomType, booking], ['checkout', 'checkout_date', 'CheckOut'])
      if (checkin === '-' && checkout === '-') {
        return '-'
      }
      return [
        checkin !== '-' ? { label: 'Check-in', value: this.formatDisplayDate(checkin) } : null,
        checkout !== '-' ? { label: 'Check-out', value: this.formatDisplayDate(checkout) } : null,
      ].filter(Boolean)
    },
    formatSupplierCancellationDetails(roomType, booking) {
      const details = this.getPathValue(roomType, 'SupplierCancellationDetails')
        || this.getPathValue(booking, 'SupplierCancellationDetails')
      const firstDetail = Array.isArray(details) && details.length ? details[0] : {}
      const fromDate = this.getFirstFieldValue([firstDetail, roomType, booking], ['SupplierFromDate', 'supplier_from_date'])
      const toDate = this.getFirstFieldValue([firstDetail, roomType, booking], ['SupplierToDate', 'supplier_to_date'])
      const charge = this.getFirstFieldValue([firstDetail, roomType, booking], ['SupplierCharge', 'supplier_charge'])
      const currency = this.getFirstFieldValue([firstDetail, roomType, booking], ['SupplierCurrency', 'supplier_currency', 'currency'])
      return [
        fromDate !== '-' ? { label: 'From', value: this.formatDisplayDateTime(fromDate) } : null,
        toDate !== '-' ? { label: 'To', value: this.formatDisplayDateTime(toDate) } : null,
        charge !== '-' ? { label: 'Charge', value: this.formatPrice(charge, currency) } : null,
      ].filter(Boolean)
    },
    formatListingCancellationPolicy(item) {
      const policy = this.getListingCancellationPolicySource(item)
      const currency = this.getFirstFieldValue(
        [policy, item],
        ['CancellationPolicyListCurrency', 'currency', 'Currency', 'SupplierCurrency', 'supplier_currency', 'SupplierCurrencyCode'],
      )
      const startDate = this.getFirstFieldValue(
        [policy, item],
        ['CancellationPolicyListStart', 'start', 'Start', 'from', 'From', 'SupplierFromDate', 'supplier_from_date'],
      )
      const endDate = this.getFirstFieldValue(
        [policy, item],
        ['CancellationPolicyListEnd', 'end', 'End', 'to', 'To', 'SupplierToDate', 'supplier_to_date'],
      )
      const amount = this.getFirstFieldValue(
        [policy, item],
        ['CancellationPolicyListAmount', 'amount', 'Amount', 'charge', 'Charge', 'SupplierCharge', 'supplier_charge'],
      )
      const displayCharge = this.getFirstFieldValue(
        [policy, item],
        ['CancellationPolicyListDisplayCharges', 'displayCharges', 'DisplayCharges', 'display_charges'],
      )

      return [
        startDate !== '-' ? { label: 'StartDate', value: startDate } : null,
        endDate !== '-' ? { label: 'EndDate', value: endDate } : null,
        amount !== '-' ? { label: 'Amount', value: this.formatPrice(amount, currency) } : null,
        displayCharge !== '-' ? { label: 'DisplyCharge', value: this.formatPrice(displayCharge, currency) } : null,
      ].filter(Boolean)
    },
    getListingCancellationPolicySource(item) {
      const policies = (item || {}).CancellationPolicyList
      if (Array.isArray(policies) && policies.length && typeof policies[0] === 'object') {
        return policies[0]
      }
      if (policies && typeof policies === 'object') {
        return policies
      }
      return {}
    },
    formatDisplayDateTime(value) {
      const formattedValue = this.formatValue(value)
      if (formattedValue === '-') {
        return '-'
      }

      const valueText = String(formattedValue).trim()
      const isoMatch = valueText.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})/)
      if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]} ${isoMatch[4]}:${isoMatch[5]}:${isoMatch[6]}`
      }

      return this.formatDisplayDate(valueText)
    },
    formatProfit(item) {
      const listingPrice = this.toNumber((item || {}).SupplierRoomRateConverted)
      const bookingPrice = this.toNumber((this.bookingDetails.room_type || {}).price)
        || this.toNumber((this.logData || {}).price)
      if (listingPrice === null || bookingPrice === null || bookingPrice === 0) {
        return '-'
      }

      const profit = bookingPrice - listingPrice
      const percentage = (profit / bookingPrice) * 100
      const currency = this.formatValue((item || {}).SupplierCurrencyCodeConverted || (this.bookingDetails.room_type || {}).currency)
      const currencyLabel = currency === '-' ? '' : ` ${currency}`
      return `${this.formatNumber(profit)}${currencyLabel} (${this.formatNumber(percentage)}%)`
    },
    toNumber(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : null
    },
    formatNumber(value) {
      const number = Number(value)
      if (!Number.isFinite(number)) {
        return '-'
      }
      return Number.isInteger(number) ? String(number) : number.toFixed(3).replace(/\.?0+$/, '')
    },
    getRefundClass(value) {
      const normalized = String(value || '').trim().toLowerCase()
      if (normalized === 'refundable') {
        return 'is-refundable'
      }
      if (normalized === 'non refundable' || normalized === 'non-refundable' || normalized === 'nonrefundable') {
        return 'is-non-refundable'
      }
      return 'is-unknown'
    },
    sortByConvertedPrice(firstItem, secondItem) {
      return this.getSortablePrice(firstItem) - this.getSortablePrice(secondItem)
    },
    getSortablePrice(item) {
      const price = Number((item || {}).SupplierRoomRateConverted)
      return Number.isFinite(price) ? price : Number.MAX_SAFE_INTEGER
    },
    goBack() {
      this.$router.push({
        name: 'priceCheckBookingResult',
        params: {
          bookingId: this.bookingId,
        },
      })
    },
    formatDateTimelisting(dateString) {
        if (!dateString) return "-";

        const datePart = dateString.substring(0, 19);
        const offset = dateString.substring(19);

        // Already formatted, no timezone offset
        if (!offset || !/^[+-]\d{2}:\d{2}$/.test(offset)) {
            return datePart.replace("T", " ");
        }

        const date = new Date(datePart + "Z");

        const offsetHours = parseInt(offset.substring(1, 3), 10);
        const offsetMinutes = parseInt(offset.substring(4, 6), 10);
        const sign = offset.startsWith("+") ? 1 : -1;

        const totalOffsetMinutes = sign * (offsetHours * 60 + offsetMinutes);

        date.setUTCMinutes(date.getUTCMinutes() + totalOffsetMinutes);

        return date.toISOString().slice(0, 19).replace("T", " ");
    },
  },
}
</script>

<style scoped>
.price-check-log-page {
  background: #f6f8fb;
  min-height: calc(100vh - 70px);
  padding: 22px;
}

.result-header {
  align-items: center;
  background: #ffffff;
  border: 1px solid #d9e4f2;
  border-radius: 8px;
  box-shadow: 0 12px 28px rgba(22, 32, 51, 0.08);
  display: flex;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 18px 20px;
}

.result-header h3 {
  color: #10213f;
  font-size: 26px;
  font-weight: 700;
  margin: 0 0 4px;
}

.header-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-chip {
  align-items: center;
  background: #edf5ff;
  border: 1px solid #cfe0f6;
  border-radius: 999px;
  color: #10213f;
  display: inline-flex;
  gap: 6px;
  min-height: 28px;
  padding: 5px 11px;
}

.meta-chip small {
  color: #64748b;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}

.meta-chip strong {
  color: #0f3f8c;
  font-size: 13px;
  font-weight: 800;
}

.back-button,
.policy-button {
  border-radius: 6px;
  font-weight: 700;
}

.booking-details-section,
.listing-section,
.data-section,
.log-loader-section {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #dbe5f1;
  border-radius: 8px;
  box-shadow: 0 10px 26px rgba(22, 32, 51, 0.08);
  margin-bottom: 20px;
  padding: 20px;
}

.room-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
}

.room-field {
  background: #f8fbff;
  border: 1px solid #dce7f4;
  border-radius: 8px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
  min-height: 78px;
  padding: 13px 14px;
}

.room-field span {
  color: #697a91;
  display: block;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.room-field strong {
  color: #10213f;
  display: block;
  font-size: 15px;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.cancellation-field {
  background: #f8fbff;
  border-color: #bfdbfe;
  grid-column: span 2;
}

.cancellation-details {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.stay-date-details {
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr;
}

.stay-date-detail,
.cancellation-detail {
  background: #ffffff;
  border: 1px solid #dbeafe;
  border-radius: 7px;
  padding: 9px 10px;
}

.stay-date-detail small,
.cancellation-detail small {
  color: #64748b;
  display: block;
  font-size: 10px;
  font-weight: 800;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.stay-date-detail strong,
.cancellation-detail strong {
  color: #0f2f5f;
  font-size: 13px;
  line-height: 1.25;
}

.section-expand-button {
  align-items: center;
  background: #edf4ff;
  border: 1px solid #cfe0f6;
  border-radius: 50%;
  color: #2f80ed;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 24px;
  font-size: 17px;
  font-weight: 700;
  height: 24px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  width: 24px;
}

.room-json-panel {
  background: #0f172a;
  border: 1px solid #263244;
  border-radius: 8px;
  margin-top: 14px;
  overflow: auto;
}

.room-json-panel pre {
  color: #dbeafe;
  font-size: 13px;
  line-height: 1.55;
  margin: 0;
  max-height: 420px;
  overflow: auto;
  padding: 16px;
  white-space: pre-wrap;
  word-break: break-word;
}

.log-loader-section {
  align-items: center;
  color: #65758b;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 340px;
  text-align: center;
}

.log-loader-section h5 {
  color: #10213f;
  font-size: 18px;
  font-weight: 700;
  margin: 14px 0 6px;
}

.log-loader-section p {
  font-size: 14px;
  margin: 0;
}

.loader-ring {
  animation: loader-spin 0.8s linear infinite;
  border: 4px solid #dbeafe;
  border-radius: 50%;
  border-top-color: #2563eb;
  height: 42px;
  width: 42px;
}

@keyframes loader-spin {
  to {
    transform: rotate(360deg);
  }
}

.section-title {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
}

.section-title h4 {
  align-items: center;
  color: #10213f;
  display: flex;
  font-size: 18px;
  font-weight: 700;
  gap: 8px;
  margin: 0;
}

.section-title h4::after {
  background: #2f80ed;
  border-radius: 99px;
  content: '';
  display: block;
  height: 3px;
  margin-top: 7px;
  width: 34px;
}

.section-title span {
  background: #edf4ff;
  border: 1px solid #cfe0f6;
  border-radius: 99px;
  color: #2f5f9c;
  font-size: 13px;
  font-weight: 600;
  padding: 5px 10px;
}

.listing-table-wrap {
  border: 1px solid #dbe5f1;
  border-radius: 8px;
  display: inline-block;
  max-height: 520px;
  max-width: 100%;
  overflow: auto;
  width: 100%;
}

.listing-table {
  border: 0;
  border-collapse: collapse;
  margin: 0 !important;
  min-width: 1320px;
  table-layout: fixed;
  width: 100%;
}

.listing-table .hotel-col {
  width: 150px;
}

.listing-table .room-type-col {
  width: 185px;
}

.listing-table .room-category-col {
  width: 165px;
}

.listing-table .refund-col {
  width: 145px;
}

.listing-table .meal-col {
  width: 145px;
}

.listing-table .supplier-col {
  width: 150px;
}

.listing-table .price-col {
  width: 140px;
}

.listing-table .profit-col {
  width: 170px;
}

.listing-table .deadline-col {
  width: 170px;
}

.listing-table th,
.listing-table td {
  border-bottom: 1px solid #e3e9f2;
  color: #26364d;
  font-size: 14px;
  padding: 11px 12px;
  text-align: left;
  vertical-align: top;
}

.deadline-cell {
  color: #10213f;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  white-space: nowrap;
}

.listing-table th {
  background: #1b006a;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  padding-bottom: 10px;
  padding-top: 10px;
  position: sticky;
  top: 0;
  text-transform: uppercase;
  z-index: 1;
}

.listing-row:hover td {
  background: #f8fbff;
}

.status-less-row td {
  background: #d1fae5;
  border-bottom-color: #86efac;
}

.listing-row.status-less-row:hover td {
  background: #bbf7d0;
}

.hotel-cell {
  align-items: flex-start;
  display: flex;
  gap: 10px;
}

.hotel-cell strong {
  color: #10213f;
}

.expand-icon {
  align-items: center;
  background: #edf4ff;
  border: 1px solid #cfe0f6;
  border-radius: 50%;
  color: #2f80ed;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 24px;
  font-size: 17px;
  font-weight: 700;
  height: 24px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  width: 24px;
}

.price-badge {
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  border-radius: 99px;
  color: #047857;
  display: inline-block;
  font-weight: 700;
  padding: 5px 10px;
  white-space: nowrap;
}

.profit-badge {
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 99px;
  color: #3730a3;
  display: inline-block;
  font-weight: 700;
  padding: 5px 10px;
  white-space: nowrap;
}

.refund-pill {
  border-radius: 99px;
  display: inline-block;
  font-weight: 700;
  padding: 5px 10px;
  white-space: nowrap;
}

.refund-pill.is-refundable {
  background: #ecfdf5;
  border: 1px solid #86efac;
  color: #047857;
}

.refund-pill.is-non-refundable {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
}

.refund-pill.is-unknown {
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  color: #475569;
}

.supplier-pill {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 99px;
  color: #9a3412;
  display: inline-block;
  font-weight: 700;
  padding: 5px 10px;
}

.supplier-policy-wrapper {
  align-items: center;
  display: inline-flex;
  flex-wrap: wrap;
  gap: 6px;
  position: relative;
}

.supplier-policy-wrapper.has-policy {
  cursor: pointer;
}

.supplier-policy-popover {
  border: 1px solid #b9c8dc;
  border-radius: 8px;
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.18);
  max-width: 360px;
  min-width: 320px;
  z-index: 2000;
}

.supplier-policy-trigger {
  align-items: center;
  border: 1px solid #58a6ff;
  border-radius: 50%;
  color: #2f80ed;
  display: inline-flex;
  font-size: 10px;
  font-weight: 800;
  height: 14px;
  justify-content: center;
  line-height: 1;
  width: 14px;
}

.supplier-policy-popover :deep(.popover-header) {
  background: linear-gradient(180deg, #f7faff 0%, #eaf1f9 100%);
  border-bottom: 1px solid #cbd8e8;
  border-radius: 8px 8px 0 0;
  padding: 0;
}

.supplier-policy-popover :deep(.popover-body) {
  background: #ffffff;
  border-radius: 0 0 8px 8px;
  padding: 12px 14px;
}

.supplier-policy-title {
  align-items: center;
  color: #10213f;
  display: flex;
  font-size: 14px;
  font-weight: 800;
  justify-content: space-between;
  line-height: 1.2;
  padding: 10px 14px;
}

.supplier-policy-row {
  align-items: flex-start;
  border-bottom: 1px solid #edf2f7;
  display: grid;
  gap: 12px;
  grid-template-columns: 105px 1fr;
  line-height: 1.35;
  margin: 0;
  padding: 8px 0;
}

.supplier-policy-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.supplier-policy-row span {
  color: #526276;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.supplier-policy-row strong {
  color: #0f2f5f;
  font-size: 13px;
  font-weight: 800;
  overflow-wrap: anywhere;
  text-align: left;
}

.expanded-json-cell {
  background: #0f172a;
  padding: 0 !important;
}

.expanded-json-cell pre {
  color: #dbeafe;
  font-size: 13px;
  line-height: 1.55;
  margin: 0;
  max-height: 420px;
  overflow: auto;
  padding: 16px;
  white-space: pre-wrap;
  word-break: break-word;
}

.empty-text {
  color: #65758b;
  font-size: 14px;
  margin: 0;
}

.data-title {
  align-items: center;
  gap: 12px;
}

.data-search-tools {
  align-items: center;
  display: flex;
  gap: 7px;
  min-width: 0;
}

.data-search {
  background: #fbfdff;
  border: 1px solid #cbd7e5;
  border-radius: 6px;
  color: #172033;
  font-size: 13px;
  height: 34px;
  max-width: 260px;
  min-width: 180px;
  outline: none;
  padding: 7px 10px;
  width: 100%;
}

.data-match-count {
  color: #65758b;
  font-size: 13px;
  font-weight: 700;
  min-width: 44px;
  text-align: right;
}

.data-search-nav {
  align-items: center;
  background: #edf4ff;
  border: 1px solid #cfe0f6;
  border-radius: 6px;
  color: #2f5f9c;
  cursor: pointer;
  display: inline-flex;
  font-size: 16px;
  font-weight: 800;
  height: 34px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  width: 34px;
}

.data-search-nav:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.json-panel {
  background: #0f172a;
  border: 1px solid #263244;
  border-radius: 8px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 8px 22px rgba(22, 32, 51, 0.12);
  overflow: auto;
  padding: 18px;
}

.json-panel pre {
  color: #dbeafe;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
  max-height: 520px;
  min-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.json-panel :deep(.json-highlight) {
  background: #fde68a;
  border-radius: 3px;
  color: #111827;
  padding: 0 2px;
}

.json-panel :deep(.json-highlight.is-active) {
  background: #f97316;
  color: #ffffff;
}

@media (max-width: 768px) {
  .price-check-log-page {
    padding: 14px;
  }

  .result-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
  }

  .booking-details-section,
  .listing-section,
  .data-section,
  .log-loader-section {
    padding: 16px;
  }

  .cancellation-field {
    grid-column: span 1;
  }

  .cancellation-details {
    grid-template-columns: 1fr;
  }
}
</style>
